/**
 * Memory Size Estimation Utilities
 * 
 * Provides accurate memory size estimation for JavaScript objects
 * with support for deep traversal and circular reference detection.
 */

import { Logger } from '@/utils/logger'

const logger = new Logger('SizeEstimator')

export interface SizeEstimationResult {
  totalBytes: number
  breakdown: {
    strings: number
    numbers: number
    booleans: number
    objects: number
    arrays: number
    functions: number
    other: number
  }
  objectCount: number
  maxDepth: number
  circularReferences: number
}

export class MemorySizeEstimator {
  private static readonly OVERHEAD = {
    OBJECT: 40,        // Base object overhead
    ARRAY: 40,         // Base array overhead  
    STRING: 40,        // String object overhead
    NUMBER: 16,        // Number object overhead
    BOOLEAN: 12,       // Boolean object overhead
    FUNCTION: 56,      // Function object overhead
    PROPERTY: 24,      // Property descriptor overhead
    ARRAY_SLOT: 8,     // Per array element overhead
  }

  /**
   * Calculate deep memory size of an object
   */
  static calculateSize(obj: any, options: {
    maxDepth?: number
    includePrototype?: boolean
    includeNonEnumerable?: boolean
  } = {}): SizeEstimationResult {
    const {
      maxDepth = 50,
      includePrototype = false,
      includeNonEnumerable = false
    } = options

    const seen = new WeakSet()
    const result: SizeEstimationResult = {
      totalBytes: 0,
      breakdown: {
        strings: 0,
        numbers: 0,
        booleans: 0,
        objects: 0,
        arrays: 0,
        functions: 0,
        other: 0
      },
      objectCount: 0,
      maxDepth: 0,
      circularReferences: 0
    }

    this.calculateDeepSize(obj, seen, result, 0, maxDepth, includePrototype, includeNonEnumerable)
    
    logger.debug('Size estimation complete', {
      totalBytes: result.totalBytes,
      totalKB: Math.round(result.totalBytes / 1024 * 100) / 100,
      objectCount: result.objectCount,
      maxDepth: result.maxDepth
    })

    return result
  }

  /**
   * Calculate size of a single value (non-recursive)
   */
  static calculateValueSize(value: any): number {
    if (value === null || value === undefined) {
      return 8 // Pointer size
    }

    switch (typeof value) {
      case 'string':
        return this.calculateStringSize(value)
      case 'number':
        return this.OVERHEAD.NUMBER
      case 'boolean':
        return this.OVERHEAD.BOOLEAN
      case 'function':
        return this.OVERHEAD.FUNCTION + (value.toString().length * 2)
      case 'object':
        if (Array.isArray(value)) {
          return this.OVERHEAD.ARRAY + (value.length * this.OVERHEAD.ARRAY_SLOT)
        }
        return this.OVERHEAD.OBJECT
      default:
        return 16 // Default for symbols, bigint, etc.
    }
  }

  /**
   * Calculate string size in memory
   */
  static calculateStringSize(str: string): number {
    // UTF-16 encoding: 2 bytes per character + object overhead
    return (str.length * 2) + this.OVERHEAD.STRING
  }

  /**
   * Estimate batch size for multiple objects
   */
  static estimateBatchSize(items: any[]): SizeEstimationResult {
    const totalResult: SizeEstimationResult = {
      totalBytes: this.OVERHEAD.ARRAY + (items.length * this.OVERHEAD.ARRAY_SLOT),
      breakdown: {
        strings: 0,
        numbers: 0,
        booleans: 0,
        objects: 0,
        arrays: 1, // The container array
        functions: 0,
        other: 0
      },
      objectCount: 1, // The container array
      maxDepth: 1,
      circularReferences: 0
    }

    for (const item of items) {
      const itemResult = this.calculateSize(item, { maxDepth: 10 })
      
      totalResult.totalBytes += itemResult.totalBytes
      totalResult.objectCount += itemResult.objectCount
      totalResult.maxDepth = Math.max(totalResult.maxDepth, itemResult.maxDepth + 1)
      totalResult.circularReferences += itemResult.circularReferences

      // Merge breakdowns
      for (const [key, value] of Object.entries(itemResult.breakdown)) {
        totalResult.breakdown[key as keyof typeof totalResult.breakdown] += value
      }
    }

    return totalResult
  }

  /**
   * Get memory usage summary
   */
  static getMemorySummary(): {
    heapUsed: number
    heapTotal: number
    external: number
    rss: number
  } {
    const usage = process.memoryUsage()
    return {
      heapUsed: usage.heapUsed,
      heapTotal: usage.heapTotal,
      external: usage.external,
      rss: usage.rss
    }
  }

  /**
   * Check if memory usage is approaching limits
   */
  static checkMemoryPressure(thresholdMB: number = 50): {
    isUnderPressure: boolean
    heapUsedMB: number
    heapTotalMB: number
    pressureRatio: number
  } {
    const usage = process.memoryUsage()
    const heapUsedMB = usage.heapUsed / (1024 * 1024)
    const heapTotalMB = usage.heapTotal / (1024 * 1024)
    const pressureRatio = heapUsedMB / thresholdMB

    return {
      isUnderPressure: heapUsedMB > thresholdMB,
      heapUsedMB,
      heapTotalMB,
      pressureRatio
    }
  }

  /**
   * Recursive size calculation with cycle detection
   */
  private static calculateDeepSize(
    obj: any,
    seen: WeakSet<object>,
    result: SizeEstimationResult,
    depth: number,
    maxDepth: number,
    includePrototype: boolean,
    includeNonEnumerable: boolean
  ): void {
    // Update max depth
    result.maxDepth = Math.max(result.maxDepth, depth)

    // Check depth limit
    if (depth > maxDepth) {
      return
    }

    // Handle primitives
    if (obj === null || obj === undefined) {
      result.breakdown.other += 8
      result.totalBytes += 8
      return
    }

    if (typeof obj !== 'object' && typeof obj !== 'function') {
      const size = this.calculateValueSize(obj)
      result.totalBytes += size
      
      switch (typeof obj) {
        case 'string':
          result.breakdown.strings += size
          break
        case 'number':
          result.breakdown.numbers += size
          break
        case 'boolean':
          result.breakdown.booleans += size
          break
        default:
          result.breakdown.other += size
      }
      return
    }

    // Check for circular references
    if (seen.has(obj)) {
      result.circularReferences++
      return
    }

    seen.add(obj)
    result.objectCount++

    // Handle functions
    if (typeof obj === 'function') {
      const functionSize = this.OVERHEAD.FUNCTION + (obj.toString().length * 2)
      result.totalBytes += functionSize
      result.breakdown.functions += functionSize
      return
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      const arrayOverhead = this.OVERHEAD.ARRAY + (obj.length * this.OVERHEAD.ARRAY_SLOT)
      result.totalBytes += arrayOverhead
      result.breakdown.arrays += arrayOverhead

      // Process array elements
      for (let i = 0; i < obj.length; i++) {
        this.calculateDeepSize(
          obj[i], 
          seen, 
          result, 
          depth + 1, 
          maxDepth, 
          includePrototype, 
          includeNonEnumerable
        )
      }
      return
    }

    // Handle objects
    const objectOverhead = this.OVERHEAD.OBJECT
    result.totalBytes += objectOverhead
    result.breakdown.objects += objectOverhead

    // Get properties to process
    const keys = includeNonEnumerable ? 
      Object.getOwnPropertyNames(obj) : 
      Object.keys(obj)

    // Process own properties
    for (const key of keys) {
      try {
        // Property key overhead
        const keySize = this.calculateStringSize(key) + this.OVERHEAD.PROPERTY
        result.totalBytes += keySize
        result.breakdown.strings += keySize

        // Property value
        const descriptor = Object.getOwnPropertyDescriptor(obj, key)
        if (descriptor && 'value' in descriptor) {
          this.calculateDeepSize(
            descriptor.value,
            seen,
            result,
            depth + 1,
            maxDepth,
            includePrototype,
            includeNonEnumerable
          )
        }
      } catch (error) {
        // Skip properties that can't be accessed
        logger.debug(`Skipping property ${key}: ${error}`)
      }
    }

    // Process prototype if requested
    if (includePrototype && obj.constructor && obj.constructor.prototype !== Object.prototype) {
      this.calculateDeepSize(
        obj.constructor.prototype,
        seen,
        result,
        depth + 1,
        maxDepth,
        includePrototype,
        includeNonEnumerable
      )
    }
  }

  /**
   * Format bytes to human readable string
   */
  static formatBytes(bytes: number): string {
    const units = ['B', 'KB', 'MB', 'GB']
    let size = bytes
    let unitIndex = 0

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024
      unitIndex++
    }

    return `${Math.round(size * 100) / 100} ${units[unitIndex]}`
  }

  /**
   * Create a memory benchmark
   */
  static benchmark(name: string, operation: () => any): {
    result: any
    memoryDelta: number
    executionTime: number
  } {
    const startMemory = process.memoryUsage().heapUsed
    const startTime = Date.now()
    
    const result = operation()
    
    const endTime = Date.now()
    const endMemory = process.memoryUsage().heapUsed
    
    return {
      result,
      memoryDelta: endMemory - startMemory,
      executionTime: endTime - startTime
    }
  }
}